import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiService } from '../utils/api';
import { LoginRequest, LoginResponse } from '../types/api';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/store';
import { onSignInSuccess, onSignOutSuccess, User } from '@/store/slices/auth/authSlice';
import { resetDeviceSelection, setSelectedDeviceId } from '@/store/slices/device/deviceSlice';

export const useAuth = () => {
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const auth = useSelector((state: RootState) => state.auth);

  // 使用Redux状态判断是否已认证
  const isAuthenticated = auth.signedIn && !!auth.token;

  // 使用useCallback包装checkAuth函数，避免不必要的重新创建
  const checkAuth = useCallback(async () => {
    try {
      setIsLoading(true);
      if (auth.token) {
        // 验证token有效性
        await apiService.post<{ user: any }>('/api/v1/auth/verify', { token: auth.token });
        
        // 只有当用户在登录页面时才自动重定向到dashboard
        if (location.pathname === '/login' || location.pathname === '/') {
          navigate('/dashboard');
        }
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      // Token无效，清除认证状态
      dispatch(onSignOutSuccess());
    } finally {
      setIsLoading(false);
    }
  }, [navigate, location.pathname, auth.token, dispatch]);

  // 只在组件挂载和路由变化时检查认证状态
  useEffect(() => {
    checkAuth();
  }, []);

  const login = async (email: string, password: string): Promise<string> => {
    try {
      setIsLoading(true);
      // The API now returns the data part directly, which is a LoginResponse
      const loginResponse = await apiService.post<LoginResponse>('/api/v1/auth/login', {
        email,
        password
      } as LoginRequest);
      
      console.log('Login response:', loginResponse);
      
      // Check if we have a valid response with a token
      if (loginResponse && typeof loginResponse === 'object') {
        let token = '';
        let user: User | null = null;
        
        // Handle different possible response structures
        if ('token' in loginResponse && 'user' in loginResponse) {
          // Direct LoginResponse format
          token = loginResponse.token;
          user = loginResponse.user as User;
        } else if ('data' in (loginResponse as any) && 
                  (loginResponse as any).data && 
                  typeof (loginResponse as any).data === 'object' && 
                  'token' in (loginResponse as any).data) {
          // Nested data format
          token = ((loginResponse as any).data as LoginResponse).token;
          user = ((loginResponse as any).data as LoginResponse).user as User;
        }
        
        if (token && user) {
          // 使用Redux存储认证信息
          dispatch(onSignInSuccess({ token, user }));

          // 如果登录响应中包含 currentDevice，则设置全局默认设备
          try {
            let defaultDeviceId: string | undefined;
            if ('currentDevice' in loginResponse) {
              defaultDeviceId = loginResponse.currentDevice?.deviceId;
            } else if ('data' in (loginResponse as any) && (loginResponse as any).data) {
              const dataPart = (loginResponse as any).data as LoginResponse;
              defaultDeviceId = dataPart.currentDevice?.deviceId;
            }

            if (defaultDeviceId) {
              dispatch(setSelectedDeviceId(defaultDeviceId));
            }
          } catch (e) {
            // 非关键路径，忽略设置默认设备的错误
            console.warn('Set default device failed:', e);
          }
          return 'SUCCESS';
        }
      }
      
      console.error('Invalid login response format:', loginResponse);
      throw new Error('Invalid login response format');
    } catch (error) {
      console.error('Login failed:', error);
      throw new Error('Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    // 使用Redux清除认证状态
    dispatch(onSignOutSuccess());
    // 清空设备选择
    dispatch(resetDeviceSelection());
    navigate('/login');
  };

  return {
    isAuthenticated,
    isLoading,
    login,
    logout,
    checkAuth,
    currentUser: auth.user,
  };
};